import React, {useEffect, useRef, useState} from 'react';
import styles from './VideoEditorUI.module.css';
import PageTitle from '../../../uiToolkit/PageTitle';
import {
  MIN_CHUNK_WIDTH_PX,
  CHUNK_GAP_PX,
  ADD_CHUNK_WIDTH_PX,
  ADD_CHUNK_MARGIN_LEFT_PX,
} from '../constants';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
// import TipBox from '../../../uiToolkit/TipBox';
import Timeline from '../components/Timeline';
import {EditChunkOption} from '../../../data/repositories/LibraryRepository';
import {VideoEditorText} from '../VideoEditorText';
// import FormattedText from '../../../uiToolkit/FormattedText/FormattedText';
import ChunkList from '../components/ChunkList';
import ChunkPreview from '../components/ChunkPreview';
import BackgroundMusicRow from '../components/BackgroundMusicRow';
import {VideoEditorState} from '../store/VideoEditorStore';
import BackButton from '../../../uiToolkit/Buttons/BackButton';
import ChunkListOverlay from '../components/ChunkListOverlay';
import Button from '../../../uiToolkit/Buttons/Button';
import {appText} from '../../../assets/text/appText';
import ErrorBox2 from '../../../uiToolkit/ErrorBox2/ErrorBox2';

interface Props {
  onBack: () => void;
  chunks: GeneratedVideoChunk[];
  selectedChunk?: GeneratedVideoChunk;
  isLoading: boolean;
  state: VideoEditorState;
  hasUnsavedChanges?: boolean;
  isEditingTemporaryDisabled?: boolean;
  onRevertUnsavedChanges?: () => void;
  onSaveUnsavedChanges?: () => void;
  onEditChunk: (option: EditChunkOption, chunkId: string) => void;
  refreshChunks: () => void;
  editChunkErrorCode?: string;
  clearErrors: () => void;
  onAddFromLibrary: () => void;
  onAddFromStoryblocks: () => void;
  newChunkIds?: Set<string>;
  forceWorking?: boolean;
  onChunksReorder: (
    chunks: GeneratedVideoChunk[],
    activeChunkId: string,
    newIndex: number,
  ) => void;
  onSelectChunk: (chunk: GeneratedVideoChunk) => void;
  hasBackgroundAudio: boolean;
  backgroundAudioName?: string;
  onRemoveBackgroundMusic: () => void;
  suppressWorkingOverlay?: boolean;
}

const EditVideoSourceUI: React.FC<Props> = ({
  onBack,
  chunks,
  selectedChunk,
  isLoading,
  state,
  hasUnsavedChanges,
  isEditingTemporaryDisabled,
  onRevertUnsavedChanges,
  onSaveUnsavedChanges,
  onEditChunk,
  refreshChunks,
  editChunkErrorCode,
  clearErrors,
  onAddFromLibrary,
  onAddFromStoryblocks,
  newChunkIds,
  forceWorking = false,
  onChunksReorder,
  onSelectChunk,
  hasBackgroundAudio,
  backgroundAudioName,
  onRemoveBackgroundMusic,
  suppressWorkingOverlay = false,
}: Props) => {
  const defaultPixelsPerSecond = 5;
  const divRef = useRef<HTMLDivElement | null>(null);

  const [totalTimelineSeconds, setTotalTimelineSeconds] = useState(0);
  const [pixelsPerSecond, setPixelsPerSecond] = useState(
    defaultPixelsPerSecond,
  );
  const [stepInSeconds, setStepInSeconds] = useState(30);
  const [intervals, setIntervals] = useState(6);
  const hasPendingChunkOperations = chunks.some(
    chunk => chunk.fileStatus !== 0,
  );
  const saveActionsDisabled =
    isEditingTemporaryDisabled ||
    hasPendingChunkOperations ||
    (!hasUnsavedChanges && state === VideoEditorState.Idle);
  const addChunkDisabled =
    isEditingTemporaryDisabled ||
    hasPendingChunkOperations ||
    state !== VideoEditorState.Idle;
  const includedChunks = chunks.filter(x => x.include);
  const visualChunksWidth = includedChunks.reduce((sum, x) => {
    return sum + Math.max(MIN_CHUNK_WIDTH_PX, x.duration * pixelsPerSecond);
  }, 0);
  const itemCount = includedChunks.length + 1;
  const totalGaps = itemCount > 1 ? CHUNK_GAP_PX * (itemCount - 1) : 0;
  const totalVisualWidthPx =
    visualChunksWidth +
    totalGaps +
    ADD_CHUNK_WIDTH_PX +
    ADD_CHUNK_MARGIN_LEFT_PX;

  useEffect(() => {
    requestAnimationFrame(() => calibrateZoom());
    const handleResize = () => {
      requestAnimationFrame(() => calibrateZoom());
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chunks]);

  const calibrateZoom = () => {
    const total = chunks.reduce(
      (sum, x) => sum + (x.include ? x.duration : 0),
      0,
    );
    let nextPixelsPerSecond = defaultPixelsPerSecond;
    let nextStepInSeconds = 30;
    let nextIntervals = 6;

    if (divRef.current && chunks.length > 0) {
      const rect = divRef.current.getBoundingClientRect();
      const clientRectWidth = rect.width;

      if (total > 0) {
        if (clientRectWidth >= total * defaultPixelsPerSecond * 4) {
          // Третий режим (сверхзум)
          nextPixelsPerSecond = 20;
          nextStepInSeconds = 5;
          nextIntervals = 5;
        } else if (clientRectWidth >= total * defaultPixelsPerSecond * 2) {
          // Double zoom
          nextPixelsPerSecond = 10;
          nextStepInSeconds = 15;
          nextIntervals = 3;
        } else {
          // Обычный режим
          nextPixelsPerSecond = 5;
          nextStepInSeconds = 30;
          nextIntervals = 6;
        }
      }
    }

    setPixelsPerSecond(nextPixelsPerSecond);
    setStepInSeconds(nextStepInSeconds);
    setIntervals(nextIntervals);
    setTotalTimelineSeconds(total);
  };

  return (
    <div ref={divRef} className={styles.videoEditorContainer}>
      <PageTitle title={VideoEditorText.title} />
      <div className={styles.headLine}>
        <BackButton overrideOnBack={onBack} />
        <div className={styles.saveResetButtons}>
          <Button
            title={appText.revertChanges}
            uiType="text"
            disabled={saveActionsDisabled}
            onClick={() => onRevertUnsavedChanges && onRevertUnsavedChanges()}
          />
          <Button
            title={appText.saveChanges}
            disabled={saveActionsDisabled}
            onClick={() => onSaveUnsavedChanges && onSaveUnsavedChanges()}
          />
        </div>
      </div>
      <ChunkPreview selectedChunk={selectedChunk} />
      <ErrorBox2
        errorCodes={editChunkErrorCode ? [editChunkErrorCode] : []}
        clearErrors={clearErrors}
      />
      {hasUnsavedChanges && (
        <div className={styles.unsavedChangesMessageContainer}>
          <p>{appText.unsavedChangesMessage}</p>
        </div>
      )}
      <div className={styles.chunksOverlay}>
        <div
          className={`${styles.chunksContainer} ${
            state !== VideoEditorState.Idle ? styles.busy : ''
          }`}>
          <div style={{width: 'max-content', minWidth: '100%'}}>
            <Timeline
              endTime={totalTimelineSeconds}
              step={stepInSeconds}
              pixelsPerSecond={pixelsPerSecond}
              intervals={intervals}
              totalWidthPx={totalVisualWidthPx}
            />
            <ChunkList
              chunks={chunks}
              selectedChunk={selectedChunk}
              onEditChunk={onEditChunk}
              onSelectChunk={onSelectChunk}
              onAddFromLibrary={onAddFromLibrary}
              onAddFromStoryblocks={onAddFromStoryblocks}
              addDisabled={addChunkDisabled}
              newChunkIds={newChunkIds}
              onChunksReorder={onChunksReorder}
              totalTimelineSeconds={totalTimelineSeconds}
              pixelsPerSecond={pixelsPerSecond}
            />
            {hasBackgroundAudio && (
              <BackgroundMusicRow
                musicName={backgroundAudioName}
                onRemove={onRemoveBackgroundMusic}
                totalWidthPx={totalVisualWidthPx}
              />
            )}
          </div>
        </div>
        <ChunkListOverlay
          state={state}
          isLoading={isLoading}
          refreshChunks={refreshChunks}
          isEditingTemporaryDisabled={isEditingTemporaryDisabled}
          forceWorking={forceWorking}
          suppressWorkingOverlay={suppressWorkingOverlay}
        />
      </div>
      {/* <TipBox
        noStandardHeader={true}
        tip={
          <>
            <div className="tipHeader">{VideoEditorText.tipHeader}</div>
            <div className="tipContent">
              <ul>
                <li>
                  <FormattedText text={VideoEditorText.tip1} />
                </li>
                <li>
                  <FormattedText text={VideoEditorText.tip2} />
                </li>
                <li>
                  <FormattedText text={VideoEditorText.tip3} />
                </li>
                <li>
                  <FormattedText text={VideoEditorText.tip4} />
                </li>
              </ul>
            </div>
          </>
        }
      /> */}
    </div>
  );
};

export default EditVideoSourceUI;
